import { c as _c } from "react/compiler-runtime";
import { sub } from 'date-fns';
import { Form, Formik, useField } from 'formik';
import { useContext } from 'react';
import { useHistory } from 'react-router';
import { CONTAINER_WIDTHS } from '@hookdeck/theme';
import { APIDestination, DestinationRateLimitPeriod } from '../../../../../../../../typings/Destination.interface';
import { DestinationTypeFeature, DestinationTypeFeatureTypesUnmanaged, isDestinationTypeFeatureUnmanaged } from '../../../../../../../../typings/Integration.interface';
import { cleanseFormErrorObject } from '../../../../../utils/form';
import { rateLimitToSeconds } from '../../../../../utils/rateLimit';
import Button from '../../../../common/base/Button';
import Container from '../../../../common/base/Container';
import Divider from '../../../../common/base/Divider';
import Icon from '../../../../common/base/Icon';
import Text from '../../../../common/base/Text';
import { formatChartRateNumber } from '../../../../common/Chart/format';
import LineChart from '../../../../common/Chart/LineChart';
import { useDialog } from '../../../../common/Dialog';
import { Div } from '../../../../common/helpers/StyledUtils';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { TeamPermission } from '../../../../contexts/TeamPermissionContext';
import useLocalStorage from '../../../../hooks/useLocalStorage';
import { DashboardContext } from '../../DashboardContext';
import { useRefreshKey } from '../../Metrics/RefreshKeyContext';
import { ChartData, useMetric } from '../../Metrics/useMetric';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog';
import destination_configuration_form_props, { DeliveryPolicyConfiguration, DestinationConfigurationFormValues, getDestinationDeliveryPolicy } from '../Forms/destination_configuration';
import resource_details_form_props from '../Forms/resource_details';
import SaveButton from '../SaveButton';
import { DeliveryGroupsFormValues, getDeliveryGroupsInitialValues, postprocessDeliveryGroupsValues, validateDeliveryGroups } from './DeliveryGroupsSettings';
type MaxDeliveryRateFeature = DestinationTypeFeatureTypesUnmanaged & {
  type: 'MAX_DELIVERY_RATE';
};
const getMaxDeliveryRateFeature = (feature: DestinationTypeFeature | undefined): MaxDeliveryRateFeature | undefined => {
  if (!feature || !isDestinationTypeFeatureUnmanaged(feature)) {
    return undefined;
  }
  return {
    ...feature,
    type: 'MAX_DELIVERY_RATE'
  };
};
const postprocessGeneralConfigurationValues = (values: DestinationConfigurationFormValues) => {
  const processed_values = destination_configuration_form_props.postprocessValues(values);
  const config_without_rate_limit = Object.fromEntries(Object.entries(processed_values.config).filter(([key]) => key !== 'rate_limit' && key !== 'rate_limit_period' && key !== 'delivery_policy'));
  return {
    ...processed_values,
    config: config_without_rate_limit
  };
};
const DeliveryGroupsFields = t0 => {
  const $ = _c(48);
  const {
    feature,
    destination
  } = t0;
  useField("enable_rate_limit");
  const [t1] = useField("config.rate_limit");
  const {
    value: rate_limit
  } = t1;
  const [t2] = useField("config.rate_limit_period");
  const {
    value: rate_limit_period
  } = t2;
  const {
    team
  } = useContext(DashboardContext);
  const [time_unit] = useLocalStorage("pref:dest:time_unit", "1d");
  const {
    refresh_key
  } = useRefreshKey();
  let normalized_rate_limit;
  let supports_delivery_groups;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== feature.delivery_groups || $[1] !== rate_limit || $[2] !== rate_limit_period || $[3] !== refresh_key || $[4] !== time_unit) {
    const metrics_now = new Date(refresh_key);
    supports_delivery_groups = feature.delivery_groups === true;
    let t9;
    if ($[13] !== rate_limit || $[14] !== rate_limit_period) {
      t9 = Number(rate_limit) && rate_limit_period && rate_limit_period !== "concurrent" ? rateLimitToSeconds(rate_limit, rate_limit_period) : undefined;
      $[13] = rate_limit;
      $[14] = rate_limit_period;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    normalized_rate_limit = t9;
    t5 = useMetric;
    t6 = "chart:line";
    t7 = "events_rate";
    t8 = refresh_key;
    t3 = sub(metrics_now, time_unit.indexOf("h") > 0 ? {
      hours: Number(time_unit.replace("h", ""))
    } : {
      days: Number(time_unit.replace("d", ""))
    }).toISOString();
    t4 = metrics_now.toISOString();
    $[0] = feature.delivery_groups;
    $[1] = rate_limit;
    $[2] = rate_limit_period;
    $[3] = refresh_key;
    $[4] = time_unit;
    $[5] = normalized_rate_limit;
    $[6] = supports_delivery_groups;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    normalized_rate_limit = $[5];
    supports_delivery_groups = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
  }
  let t9;
  if ($[16] !== t3 || $[17] !== t4) {
    t9 = {
      min: t3,
      max: t4
    };
    $[16] = t3;
    $[17] = t4;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== destination.id) {
    t10 = {
      filters: {
        destination_id: [destination.id]
      }
    };
    $[19] = destination.id;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t9) {
    t11 = {
      date_range: t9,
      options: t10
    };
    $[21] = t10;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const events_rate = t5(t6, t7, t8, t11);
  const events_rate_data = events_rate.data;
  const events_rate_loading = events_rate_data === undefined;
  let t12;
  if ($[24] !== events_rate_data) {
    t12 = !!events_rate_data?.data && events_rate_data.data.some(_temp);
    $[24] = events_rate_data;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const has_events_rate_data = t12;
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      gap: 4
    };
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      px: CONTAINER_WIDTHS.medium
    };
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== destination.id || $[29] !== feature || $[30] !== supports_delivery_groups) {
    t15 = <DeliveryPolicyConfiguration feature={feature} prefix="" destination_id={destination.id} show_delivery_groups={supports_delivery_groups} />;
    $[28] = destination.id;
    $[29] = feature;
    $[30] = supports_delivery_groups;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <SaveButton m={{
      t: 5
    }} />;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t15) {
    t17 = <Div w={t14}>{t15}{t16}</Div>;
    $[33] = t15;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  let t19;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      px: 420
    };
    t19 = {
      l: 4
    };
    $[35] = t18;
    $[36] = t19;
  } else {
    t18 = $[35];
    t19 = $[36];
  }
  let t20;
  if ($[37] !== events_rate_data || $[38] !== events_rate_loading || $[39] !== has_events_rate_data || $[40] !== normalized_rate_limit || $[41] !== team?.max_events_per_second) {
    t20 = !events_rate_loading && !has_events_rate_data ? undefined : <><Text size="xs" semi={true} muted={true} m={{
        b: 1,
        top: 2,
        l: 4
      }}><Icon icon="info" muted={true} left={1} /> Historical events per second</Text><LineChart legend_placeholder="Max new events per second" height={240} loading={events_rate_loading} datasets={events_rate_data ? [{
        key: "events",
        label: "Events per second",
        theme_color: "primary" as const,
        mono: false,
        data: events_rate_data.data,
        total: events_rate_data.total,
        highest: events_rate_data.highest,
        average: events_rate_data.average,
        getDataLabel: _temp2,
        metric_type: "max" as const
      }, {
        key: "limit",
        label: "Delivery rate limit",
        theme_color: "danger" as const,
        mono: false,
        dashed: true,
        data: events_rate_data.data.map(d => ({
          ...d,
          y: normalized_rate_limit ? Math.min(normalized_rate_limit, team?.max_events_per_second || 0) : team?.max_events_per_second || 0
        })),
        total: (normalized_rate_limit ? Math.min(normalized_rate_limit, team?.max_events_per_second || 0) : team?.max_events_per_second || 0) * events_rate_data.data.length,
        highest: normalized_rate_limit ? Math.min(normalized_rate_limit, team?.max_events_per_second || 0) : team?.max_events_per_second || 0,
        average: normalized_rate_limit ? Math.min(normalized_rate_limit, team?.max_events_per_second || 0) : team?.max_events_per_second || 0,
        getDataLabel: _temp3,
        metric_type: "rate" as const
      }] : []} /></>;
    $[37] = events_rate_data;
    $[38] = events_rate_loading;
    $[39] = has_events_rate_data;
    $[40] = normalized_rate_limit;
    $[41] = team?.max_events_per_second;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t20) {
    t21 = <Div w={t18} m={t19}>{t20}</Div>;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== t17 || $[46] !== t21) {
    t22 = <Div flex={t13}>{t17}{t21}</Div>;
    $[45] = t17;
    $[46] = t21;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  return t22;
};
const shouldConfirmDeliveryGroupPolicySave = (destination: APIDestination, values: DeliveryGroupsFormValues) => {
  if ('delivery_policy' in destination.config && destination.config.delivery_policy?.groups && !values.enable_delivery_groups) {
    return true;
  }
  const current_key = getDestinationDeliveryPolicy(destination)?.groups?.key?.trim() || '';
  const next_key = values.config.delivery_groups.key?.trim() || '';
  return !!next_key && current_key !== next_key;
};
const DeliveryGroupNonRetroactiveWarning = () => {
  const $ = _c(4);
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      direction: "column",
      gap: 3
    };
    t1 = <Text>Delivery group changes only apply to events received after this update is saved.</Text>;
    t2 = <Text>Events received before this update remain associated with their previous delivery group.</Text>;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Div flex={t0}>{t1}{t2}<Text>Events received before delivery groups were enabled have no delivery group. If they are retried, Hookdeck schedules them in the reserved{" "}<Text as="span" mono={true}>__missing__</Text>{" "}group.</Text></Div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
export const DestinationSettings = t0 => {
  const $ = _c(91);
  const {
    destination,
    mutate
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    destination_types,
    team
  } = useContext(DashboardContext);
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  const history = useHistory();
  const t1 = destination_types?.[destination.type]?.features.MAX_DELIVERY_RATE;
  let t2;
  if ($[0] !== t1) {
    t2 = getMaxDeliveryRateFeature(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const max_delivery_rate_feature = t2;
  const supports_delivery_groups = max_delivery_rate_feature?.delivery_groups === true;
  let t3;
  if ($[2] !== HookdeckAPI.destinations || $[3] !== addToast || $[4] !== destination || $[5] !== mutate) {
    t3 = async values => HookdeckAPI.destinations.update(destination.id, values).then(destination_updated => {
      const destination_merged = {
        ...destination,
        ...destination_updated
      };
      mutate(destination_merged);
      addToast("success", "Destination saved");
      return destination_merged;
    }).catch(e => {
      addToast("error", `An error occurred while saving the destination${e.response?.data[0] ? `: ${e.response.data[0]}` : ""}`);
      return values;
    });
    $[2] = HookdeckAPI.destinations;
    $[3] = addToast;
    $[4] = destination;
    $[5] = mutate;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const onUpdate = t3;
  let t4;
  if ($[7] !== HookdeckAPI.destinations || $[8] !== addToast || $[9] !== destination || $[10] !== mutate) {
    t4 = async () => {
      HookdeckAPI.destinations.enable(destination.id).then(updated_destination => {
        mutate({
          ...destination,
          ...updated_destination
        });
        addToast("success", "Destination enabled");
      }).catch(() => {
        addToast("error", "Failed to enable destination");
      });
    };
    $[7] = HookdeckAPI.destinations;
    $[8] = addToast;
    $[9] = destination;
    $[10] = mutate;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const onEnable = t4;
  let t5;
  if ($[12] !== HookdeckAPI.destinations || $[13] !== addToast || $[14] !== destination || $[15] !== mutate || $[16] !== showDialog) {
    t5 = async () => {
      showDialog(() => {
        HookdeckAPI.destinations.disable(destination.id).then(updated_destination_0 => {
          mutate({
            ...destination,
            ...updated_destination_0
          });
          addToast("success", "Destination disabled");
        }).catch(() => {
          addToast("error", "Failed to disable destination");
        });
      }, _temp4, {
        title: "Disable destination",
        submit_label: "Disable",
        cancel_label: "Cancel",
        message: "By disabling this destination, all connections associated with this destination will also be disabled. Are you sure?"
      });
    };
    $[12] = HookdeckAPI.destinations;
    $[13] = addToast;
    $[14] = destination;
    $[15] = mutate;
    $[16] = showDialog;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const onDisable = t5;
  let t6;
  if ($[18] !== HookdeckAPI.destinations || $[19] !== addToast || $[20] !== destination.id || $[21] !== history || $[22] !== showDialog) {
    t6 = async () => {
      showDialog(() => {
        HookdeckAPI.destinations.delete(destination.id).then(() => {
          addToast("success", "Destination deleted");
          history.push("/destinations");
        }).catch(() => {
          addToast("error", "Failed to delete destination");
        });
      }, _temp5, {
        danger: true,
        title: "Delete destination",
        submit_label: "Delete",
        cancel_label: "Keep destination",
        message: <ConfirmDeleteDialog text="By deleting this destination, no events will be delivered anymore. All connections associated with this destination will also be deleted. Are you sure?" />
      });
    };
    $[18] = HookdeckAPI.destinations;
    $[19] = addToast;
    $[20] = destination.id;
    $[21] = history;
    $[22] = showDialog;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const onDelete = t6;
  let t7;
  if ($[24] !== addToast || $[25] !== onUpdate || $[26] !== supports_delivery_groups || $[27] !== team?.max_events_per_second) {
    t7 = (values_0, resetForm) => onUpdate(postprocessDeliveryGroupsValues(values_0, supports_delivery_groups)).then(destination_0 => {
      resetForm({
        values: getDeliveryGroupsInitialValues(destination_0, team?.max_events_per_second)
      });
    }).catch(e_0 => {
      addToast("error", `An error occurred while saving the delivery policy${e_0.response?.data[0] ? `: ${e_0.response.data[0]}` : ""}`);
      return resetForm({
        values: values_0
      });
    });
    $[24] = addToast;
    $[25] = onUpdate;
    $[26] = supports_delivery_groups;
    $[27] = team?.max_events_per_second;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const saveDeliveryPolicy = t7;
  let t8;
  if ($[29] !== destination || $[30] !== saveDeliveryPolicy || $[31] !== showDialog) {
    t8 = (values_1, t9) => {
      const {
        resetForm: resetForm_0,
        setSubmitting
      } = t9;
      const delivery_policy_values = values_1 as DeliveryGroupsFormValues;
      const submit = () => saveDeliveryPolicy(delivery_policy_values, resetForm_0);
      if (shouldConfirmDeliveryGroupPolicySave(destination, delivery_policy_values)) {
        showDialog(submit, () => setSubmitting(false), {
          title: "Delivery group changes are not retroactive",
          message: <DeliveryGroupNonRetroactiveWarning />,
          submit_label: "Save delivery policy",
          cancel_label: "Cancel"
        });
        return;
      }
      return submit();
    };
    $[29] = destination;
    $[30] = saveDeliveryPolicy;
    $[31] = showDialog;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const onSubmitDeliveryPolicy = t8;
  const t9 = `${destination.id}-details`;
  let t10;
  if ($[33] !== destination) {
    t10 = resource_details_form_props.getInitialValues(destination);
    $[33] = destination;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== HookdeckAPI.destinations || $[36] !== destination.name) {
    t11 = values_2 => resource_details_form_props.validate(values_2, name => name === destination.name ? Promise.resolve(false) : HookdeckAPI.destinations.nameIsUsed(name));
    $[35] = HookdeckAPI.destinations;
    $[36] = destination.name;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== onUpdate) {
    t12 = (values_3, t13) => {
      const {
        resetForm: resetForm_1
      } = t13;
      return onUpdate(resource_details_form_props.postprocessValues(values_3)).then(destination_1 => {
        resetForm_1({
          values: resource_details_form_props.getInitialValues(destination_1)
        });
      });
    };
    $[38] = onUpdate;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  let t14;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Text bold={true} size="l" m={{
      t: 6,
      b: 4
    }}>Details</Text>;
    t14 = <resource_details_form_props.Fields prefix="" placeholder="my-api" name_prefix="Destination" show_description={true} description_help="Optionally provide a description for this resource." help="Recommended for API use, or if you have multiple destinations with the same provider." />;
    $[40] = t13;
    $[41] = t14;
  } else {
    t13 = $[40];
    t14 = $[41];
  }
  let t15;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Form>{t13}{t14}<SaveButton m={{
        t: 5
      }} /></Form>;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t10 || $[44] !== t11 || $[45] !== t12 || $[46] !== t9) {
    t16 = <Container medium={true} left={true}><TeamPermission role="member"><Formik key={t9} initialValues={t10} validate={t11} onSubmit={t12}>{t15}</Formik></TeamPermission></Container>;
    $[43] = t10;
    $[44] = t11;
    $[45] = t12;
    $[46] = t9;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Div m={{
      x: 6,
      t: 10,
      b: 8
    }}><Divider /></Div>;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Text as="h2" size="l" bold={true} m={{
      b: 4
    }}>Configuration</Text>;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  const t19 = `${destination.id}-config`;
  let t20;
  if ($[50] !== destination) {
    t20 = destination_configuration_form_props.getInitialValues(destination);
    $[50] = destination;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== onUpdate) {
    t21 = (values_5, t22) => {
      const {
        resetForm: resetForm_2
      } = t22;
      return onUpdate(postprocessGeneralConfigurationValues(values_5 as unknown as DestinationConfigurationFormValues)).then(destination_2 => {
        resetForm_2({
          values: destination_configuration_form_props.getInitialValues(destination_2)
        });
      });
    };
    $[52] = onUpdate;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <destination_configuration_form_props.Fields prefix="" disable_name={true} hide_max_delivery_rate={true} advanced_configuration_display="section" />;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Form>{t22}<SaveButton m={{
        t: 5
      }} /></Form>;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t19 || $[57] !== t20 || $[58] !== t21) {
    t24 = <Container medium={true} left={true}><TeamPermission role="member">{t18}<Formik key={t19} initialValues={t20} validate={_temp6} onSubmit={t21}>{t23}</Formik></TeamPermission></Container>;
    $[56] = t19;
    $[57] = t20;
    $[58] = t21;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== destination || $[61] !== max_delivery_rate_feature || $[62] !== onSubmitDeliveryPolicy || $[63] !== supports_delivery_groups || $[64] !== team?.max_events_per_second) {
    t25 = max_delivery_rate_feature && <><Div m={{
        x: 6,
        t: 10,
        b: 8
      }}><Divider /></Div><Container xxlarge={true} left={true}><TeamPermission role="member"><Text as="h2" size="l" bold={true} m={{
            b: 0
          }}>Delivery policy</Text><Text as="p" muted={true} m={{
            b: 4,
            t: 0
          }} max_w={{
            px: CONTAINER_WIDTHS.medium
          }}>Manage the event delivery queue to this destination.</Text><Formik key={`${destination.id}-delivery-policy`} initialValues={getDeliveryGroupsInitialValues(destination, team?.max_events_per_second)} validate={values_6 => validateDeliveryGroups(values_6 as DeliveryGroupsFormValues, supports_delivery_groups)} onSubmit={onSubmitDeliveryPolicy}><Form><DeliveryGroupsFields feature={max_delivery_rate_feature} destination={destination} /></Form></Formik></TeamPermission></Container></>;
    $[60] = destination;
    $[61] = max_delivery_rate_feature;
    $[62] = onSubmitDeliveryPolicy;
    $[63] = supports_delivery_groups;
    $[64] = team?.max_events_per_second;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  let t26;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Div m={{
      x: 6,
      t: 10,
      b: 8
    }}><Divider /></Div>;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      b: 20
    };
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  const t28 = destination.disabled_at ? "Enable" : "Disable";
  let t29;
  if ($[68] !== t28) {
    t29 = <Text as="h2" size="l" bold={true} m={0}>{t28} destination</Text>;
    $[68] = t28;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  let t30;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = {
      b: 4
    };
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  const t31 = destination.disabled_at ? "Enabling your destination will resume delivery of new events to this destination" : "Disabling your destination will stop all event delivery to this destination";
  let t32;
  if ($[71] !== t31) {
    t32 = <Text as="p" muted={true} m={t30}>{t31}</Text>;
    $[71] = t31;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  const t33 = destination.disabled_at ? onEnable : onDisable;
  const t34 = destination.disabled_at ? "add_circle" : "disable";
  const t35 = destination.disabled_at ? "Enable" : "Disable";
  let t36;
  if ($[73] !== t33 || $[74] !== t34 || $[75] !== t35) {
    t36 = <Button.Permission outline={true} onClick={t33} icon={t34}>{t35} destination</Button.Permission>;
    $[73] = t33;
    $[74] = t34;
    $[75] = t35;
    $[76] = t36;
  } else {
    t36 = $[76];
  }
  let t37;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <Text as="h2" size="l" bold={true} m={{
      t: 10,
      b: 0
    }}>Delete destination</Text>;
    $[77] = t37;
  } else {
    t37 = $[77];
  }
  let t38;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <Text as="p" muted={true} m={{
      b: 4
    }}>Deleting this destination will permanently delete it and all associated connections.</Text>;
    $[78] = t38;
  } else {
    t38 = $[78];
  }
  let t39;
  if ($[79] !== onDelete) {
    t39 = <Button.Permission danger={true} outline={true} onClick={onDelete} icon="delete">Delete destination</Button.Permission>;
    $[79] = onDelete;
    $[80] = t39;
  } else {
    t39 = $[80];
  }
  let t40;
  if ($[81] !== t29 || $[82] !== t32 || $[83] !== t36 || $[84] !== t39) {
    t40 = <Container medium={true} left={true} m={t27}><TeamPermission role="member">{t29}{t32}{t36}{t37}{t38}{t39}</TeamPermission></Container>;
    $[81] = t29;
    $[82] = t32;
    $[83] = t36;
    $[84] = t39;
    $[85] = t40;
  } else {
    t40 = $[85];
  }
  let t41;
  if ($[86] !== t16 || $[87] !== t24 || $[88] !== t25 || $[89] !== t40) {
    t41 = <Div>{t16}{t17}{t24}{t25}{t26}{t40}</Div>;
    $[86] = t16;
    $[87] = t24;
    $[88] = t25;
    $[89] = t40;
    $[90] = t41;
  } else {
    t41 = $[90];
  }
  return t41;
};
function _temp(data) {
  return Number.isFinite(Number(data.y));
}
function _temp2(v) {
  return `${formatChartRateNumber(v, "second")} / second`;
}
function _temp3(v_0) {
  return `${formatChartRateNumber(v_0, "second")} / second`;
}
function _temp4() {
  return null;
}
function _temp5() {
  return null;
}
async function _temp6(values_4) {
  return cleanseFormErrorObject(await destination_configuration_form_props.validate(values_4));
}